import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { getInitials } from "@/utils/strings";
import { TLiveAgent } from "@/models/call.center.model";
import { CALLCENTER_USER_STATUS_IN_CALL, CALLCENTER_USER_STATUS_NOT_AVAILABLE, CALLCENTER_USER_STATUS_OFFLINE, CALLCENTER_USER_STATUS_READY } from "@icebergeradmin/common";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

const MAX_AVATARS_TO_SHOW = 3;

interface LiveAgentsAvatarGroupProps {
    total: number;
    liveAgents?: TLiveAgent[];
    loading?: boolean;
}

const AGENT_STATUS_LABELS = {
    [CALLCENTER_USER_STATUS_READY]: { label: "Ready", color: "text-[#4da3ff]" },
    [CALLCENTER_USER_STATUS_NOT_AVAILABLE]: { label: "Not Ready", color: "text-[#ff6b6b]" },
    [CALLCENTER_USER_STATUS_IN_CALL]: { label: "In Call", color: "text-[#4da3ff]" },
    [CALLCENTER_USER_STATUS_OFFLINE]: { label: "Offline", color: "text-[#ff6b6b]" },
}

export default function LiveAgentsAvatarGroup({
    total,
    liveAgents = [],
    loading,
}: LiveAgentsAvatarGroupProps) {
    if (loading) {
        return (
            <div className="flex items-center gap-1 lg:gap-2">
                <div className="flex -space-x-3">
                    {Array.from({ length: MAX_AVATARS_TO_SHOW }).map((_, i) => (
                        <Skeleton key={i} className="h-6 w-6 xl:h-8 xl:w-8 rounded-full ring-2 ring-background" />
                    ))}
                </div>
                <Skeleton className="h-3 w-16" />
            </div>
        );
    }

    const availableAgents = liveAgents.filter(
        (a) => a.status === CALLCENTER_USER_STATUS_IN_CALL || a.status === CALLCENTER_USER_STATUS_READY
    );
    const agentsToShow = liveAgents.slice(0, MAX_AVATARS_TO_SHOW);

    return (
        <HoverCard openDelay={200} closeDelay={150}>
            <HoverCardTrigger asChild>
                <button
                    type="button"
                    aria-label="Show live agents status"
                    className="flex items-center gap-1 lg:gap-2 cursor-pointer bg-transparent border-0 p-0 text-left"
                >
                    <div className="flex -space-x-3 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background *:data-[slot=avatar]:grayscale">
                        {agentsToShow.map((u) => (
                            <Avatar key={u.id} className="h-6 w-6 xl:h-8 xl:w-8">
                                <AvatarImage src={u.avatar || ""} />
                                <AvatarFallback>
                                    {getInitials(`${u.firstName} ${u.lastName || ""}`)}
                                </AvatarFallback>
                            </Avatar>
                        ))}
                    </div>
                    <div className="text-gray-400 text-xs whitespace-nowrap md:whitespace-normal">
                        {availableAgents.length} of {total}{" "}
                        <span className="text-white text-xs whitespace-nowrap">at work</span>
                    </div>
                </button>
            </HoverCardTrigger>

            {total > 0 && <HoverCardContent
                side="bottom"
                align="start"
                className="w-64 p-0 bg-ice rounded-xl"
            >
                <ScrollArea className="[&>[data-radix-scroll-area-viewport]>div]:!block [&>[data-radix-scroll-area-viewport]]:max-h-72">
                    <div className="flex flex-col py-2">
                        {liveAgents.map((agent) => {
                            const fullName = `${agent.firstName} ${agent.lastName || ""}`.trim();

                            return (
                                <div
                                    key={agent.id}
                                    className="flex items-center gap-3 px-4 py-2"
                                >
                                    <Avatar className="h-10 w-10 shrink-0">
                                        <AvatarImage src={agent.avatar || ""} />
                                        <AvatarFallback>{getInitials(fullName)}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-col min-w-0 overflow-hidden">
                                        <span className="text-sm text-white truncate">
                                            {fullName}
                                        </span>
                                        <span className={cn("text-xs", AGENT_STATUS_LABELS[agent.status as keyof typeof AGENT_STATUS_LABELS].color)}>
                                            {AGENT_STATUS_LABELS[agent.status as keyof typeof AGENT_STATUS_LABELS].label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>
            </HoverCardContent>
            }
        </HoverCard>
    );
}